import { useEffect } from "react";

interface UseReviewKeyboardParams {
  visible: boolean;
  rejectMode: boolean;
  onClose: () => void;
  cancelRejectMode: () => void;
  handleRejectConfirm: () => void;
  goPrevAttachment: () => void;
  goNextAttachment: () => void;
  handleAccept: () => void;
  enterRejectMode: () => void;
}

export const useReviewKeyboard = ({
  visible,
  rejectMode,
  onClose,
  cancelRejectMode,
  handleRejectConfirm,
  goPrevAttachment,
  goNextAttachment,
  handleAccept,
  enterRejectMode,
}: UseReviewKeyboardParams) => {
  useEffect(() => {
    if (!visible) return;

    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      const inField = tag === "TEXTAREA" || tag === "INPUT";

      if (e.key === "Escape") {
        e.preventDefault();
        if (rejectMode) cancelRejectMode();
        else onClose();
        return;
      }

      // While typing in the comment box, only Cmd/Ctrl+Enter submits
      if (inField) {
        if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
          e.preventDefault();
          handleRejectConfirm();
        }
        return;
      }

      // RTL: Right = previous, Left = next (mirrors MediaSlider)
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goPrevAttachment();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        goNextAttachment();
      } else if (e.key === "a" || e.key === "A") {
        e.preventDefault();
        handleAccept();
      } else if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        enterRejectMode();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [
    visible,
    rejectMode,
    onClose,
    cancelRejectMode,
    handleRejectConfirm,
    goPrevAttachment,
    goNextAttachment,
    handleAccept,
    enterRejectMode,
  ]);
};
